import { useDefenseBlueprints, type DefenseProjectionRow, type DefenseStatsDto } from '@/api/hooks'
import { StatIcon, ScopeIcon, GoldIcon, WoodIcon, RumIcon } from '@/components/game/GameIcons'
import { formatDuration } from '@/lib/time'
import { isInProjectionWindow } from '@/lib/projectionWindow'

interface Props {
  /** Defence structure key (e.g. "militia"). */
  type: string
  /** The player's current level for this structure — highlighted in the table. */
  currentLevel: number
}

const STAT_ORDER: (keyof DefenseStatsDto)[] = ['manoeuvre', 'bordee', 'abordage', 'armure', 'hp']

/**
 * Level-by-level forecast for one defence structure, so the player can plan ahead:
 * combat profile (manoeuvre/bordée/abordage/armure/coque + the Tour's rerolls),
 * cost and build time for every reachable level. Mirror of
 * {@see BuildingProjectionTable} — defences are decapped, the table is scrollable.
 */
export function DefenseProjectionTable({ type, currentLevel }: Props) {
  const { data, isLoading, isError } = useDefenseBlueprints()
  const blueprint = data?.structures.find((s) => s.type === type)

  if (isLoading) {
    return <p className="text-xs text-ocean-300 py-2">Chargement de l'évolution…</p>
  }
  if (isError || !blueprint) {
    return <p className="text-xs text-rum-500 py-2">Évolution indisponible.</p>
  }

  // Only show the stat columns this structure actually uses (any non-zero level).
  const shownStats = STAT_ORDER.filter((name) => blueprint.projection.some((r) => r[name] > 0))
  const showRerolls = blueprint.projection.some((r) => r.rerolls > 0)

  return (
    <div className="mt-2 max-h-64 overflow-auto rounded border border-ocean-500/20 bg-ocean-900/40">
      <table className="w-full min-w-[320px] text-xs text-ocean-100">
        <thead className="sticky top-0 bg-ocean-900 text-ocean-300">
          <tr>
            <th className="px-2 py-1.5 text-left font-semibold">Niv.</th>
            <th className="px-2 py-1.5 text-left font-semibold">Profil</th>
            <th className="px-2 py-1.5 text-right font-semibold">Coût</th>
            <th className="px-2 py-1.5 text-right font-semibold">Temps</th>
          </tr>
        </thead>
        <tbody>
          {blueprint.projection.map((row, idx) => {
            // Fenêtre glissante : ±5 paliers autour du niveau courant (défenses décapées).
            if (!isInProjectionWindow(row.level, currentLevel)) return null
            const prev = idx > 0 ? blueprint.projection[idx - 1] : null
            const isCurrent = row.level === currentLevel
            const isNext = row.level === currentLevel + 1
            return (
              <tr
                key={row.level}
                className={
                  isCurrent
                    ? 'bg-sand-500/20 text-sand-100'
                    : isNext
                      ? 'bg-ocean-500/10'
                      : 'odd:bg-ocean-700/20'
                }
              >
                <td className="px-2 py-1 whitespace-nowrap align-top">
                  {row.level}
                  {isCurrent && <span className="ml-1 text-[10px] text-sand-300">actuel</span>}
                  {isNext && <span className="ml-1 text-[10px] text-ocean-300">suivant</span>}
                </td>
                <td className="px-2 py-1">
                  <ProfileCell row={row} prev={prev} shownStats={shownStats} showRerolls={showRerolls} />
                </td>
                <td className="px-2 py-1 text-right whitespace-nowrap align-top">
                  <span className="inline-flex items-center justify-end gap-1">
                    <GoldIcon className="w-3.5 h-3.5 text-sand-300" />{row.upgradeCost.gold.toLocaleString('fr-FR')}
                    <WoodIcon className="w-3.5 h-3.5 text-sand-300 ml-1.5" />{row.upgradeCost.wood.toLocaleString('fr-FR')}
                    <RumIcon className="w-3.5 h-3.5 text-sand-300 ml-1.5" />{row.upgradeCost.rhum.toLocaleString('fr-FR')}
                  </span>
                </td>
                <td className="px-2 py-1 text-right whitespace-nowrap text-ocean-300 align-top">
                  {formatDuration(row.upgradeTimeSeconds)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function ProfileCell({
  row,
  prev,
  shownStats,
  showRerolls,
}: {
  row: DefenseProjectionRow
  prev: DefenseProjectionRow | null
  shownStats: (keyof DefenseStatsDto)[]
  showRerolls: boolean
}) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {shownStats.map((name) => {
        const gain = row[name] - (prev?.[name] ?? 0)
        // Manoeuvre on a static structure = the Watchtower's "Vigie" (spotting → fire first).
        const isVigie = name === 'manoeuvre'
        return (
          <span key={name} className="inline-flex items-center gap-0.5 whitespace-nowrap">
            {isVigie ? (
              <ScopeIcon className="w-3.5 h-3.5 text-sand-300" />
            ) : (
              <StatIcon name={name} className="w-3.5 h-3.5 text-sand-300" />
            )}
            {row[name]}
            {gain > 0 && prev && <span className="text-[10px] text-green-300">+{gain}</span>}
          </span>
        )
      })}
      {showRerolls && row.rerolls > 0 && (
        <span className="inline-flex items-center gap-0.5 whitespace-nowrap text-sand-200" title="Relances de dés ratés (Vigie)">
          ↻{row.rerolls}
        </span>
      )}
    </span>
  )
}
